import React, { useEffect, useState } from "react";
import { Table, Popconfirm, Space } from "antd";
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  Modal,
  TextField,
  Avatar,
  Typography,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DeleteOutlined, EditOutlined, FileAddTwoTone } from "@ant-design/icons";
import { ScheduleMessageApiService } from "../../services/api/ScheduleMessageAPIService";
import {
  API_ERROR_MESSAGE,
  API_SUCCESS_MESSAGE,
  BUTTON,
  TABLE_HEADING,
} from "../../shared/constants";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function PaymentUpdatesMessages() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTime, setSelectedTime] = useState(dayjs());
  const [snackData, setSnackData] = useState({
    show: false,
    message: "",
    type: "error",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [form, setForm] = useState({ day: "", schedule_time: "", message: "" });
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    fetchMessageDetails();
  }, []);

  const fetchMessageDetails = () => {
    setLoading(true);
    ScheduleMessageApiService.messageListing("payment_updates")
      .then((response) => {
        setData(response);
      })
      .catch((errResponse) => {
        setSnackData({
          show: true,
          message:
            errResponse?.response?.data?.message ||
            API_ERROR_MESSAGE.INTERNAL_SERVER_ERROR,
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  const handleAddNew = () => {
    setIsEdit(false);
    setForm({ day: "", schedule_time: "", message: "" });
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setIsEdit(true);
    setForm({ ...record });
    setIsModalOpen(true);
  };

  const handleDelete = (record) => {
    console.log("record", record);
    ScheduleMessageApiService.messageDelete(
      "payment_updates",
      record.id,
      record.day
    )
      .then(() => {
        setSnackData({
          show: true,
          message: "Deleted successfully!",
          type: "success",
        });
        fetchMessageDetails();
      })
      .catch(() =>
        setSnackData({
          show: true,
          message: "Failed to delete",
          type: "error",
        })
      );
  };

  const handleSave = () => {
    let payload = {};

    if (isEdit) {
      payload = {
        schedule_time: form.schedule_time,
        message: form.message,
      };
    } else {
      payload = {
        id: uuidv4().replace(/-/g, ""),
        day: +form.day,
        schedule_time: form.schedule_time,
        message: form.message,
      };
    }

    const apiCall = isEdit
      ? ScheduleMessageApiService.messageEdit(
          "payment_updates",
          payload,
          form.id,
          form.day,
          ""
        )
      : ScheduleMessageApiService.messageCreation(
          "payment_updates",
          payload,
          ""
        );

    apiCall
      .then(() => {
        setSnackData({
          show: true,
          message: isEdit ? "Updated successfully!" : "Created successfully!",
          type: "success",
        });
        fetchMessageDetails();
        setIsModalOpen(false);
      })
      .catch(() =>
        setSnackData({
          show: true,
          message: "API Error!",
          type: "error",
        })
      );
  };

  const columns = [
    {
      title: TABLE_HEADING.DAY,
      dataIndex: "day",
      width: "10%",
    },
    {
      title: TABLE_HEADING.SCHEDULE_TIME,
      dataIndex: "schedule_time",
      width: "20%",
      render: (schedule_time) => {
        const utcDateTime = dayjs.utc(`${dayjs().format("YYYY-MM-DD")}T${schedule_time}`);
        const cstTime = utcDateTime.tz("America/Chicago").format("hh:mm A [CST]");
        return cstTime;
      },
    },
    {
      title: TABLE_HEADING.MESSAGE,
      dataIndex: "message",
    },
    {
      title: TABLE_HEADING.ACTION,
      key: "action",
      render: (record) => (
        <Space>
          <Button
            onClick={() => handleEdit(record)}
            startIcon={<EditOutlined />}
          >
            {BUTTON.EDIT}
          </Button>
          <Popconfirm
            title="Are you sure to delete this message?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button startIcon={<DeleteOutlined style={{ color: "red" }} />}>
              {" "}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "blue", width: 48, height: 48, fontSize: 20 }}>
            P
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            Payment <Typography component="span">– Messages</Typography>
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileAddTwoTone />}
          onClick={handleAddNew}
        >
          Add New
        </Button>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Table
          rowKey="day"
          className="custom-table"
          columns={columns}
          dataSource={data}
        />
      )}

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEdit ? "Update" : "Add new"}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} p={2}>
            <TextField
              label="Day"
              value={form.day}
              onChange={(e) => setForm({ ...form, day: e.target.value })}
              type="number"
              fullWidth
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label="Schedule Time (UTC)"
                    value={
                                          form.schedule_time
                                            ? dayjs
                                                .utc(`${dayjs().format("YYYY-MM-DD")}T${form.schedule_time}`)
                                            : null
                                        }
                    onChange={(newValue) => {
                      if (newValue) {
                        const timeOnly = newValue.format("HH:mm:ss"); // e.g., "14:30:00"
                        setSelectedTime(newValue);
                        setForm({ ...form, schedule_time: timeOnly });
                      }
                    }}
                    renderInput={(params) => <TextField
                                            {...params}
                                            fullWidth
                                            helperText="Time is stored and shown in UTC"
                                          />}
                  />
                </LocalizationProvider>
            <TextField
              label="Message"
              value={form.message}
              multiline
              minRows={4}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
            <Button variant="outlined" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        style={{ top: "80px" }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackData.show}
        autoHideDuration={3000}
        onClose={() => setSnackData({ show: false })}
      >
        <Alert
          severity={snackData.type}
          onClose={() => setSnackData({ show: false })}
        >
          {snackData.message}
        </Alert>
      </Snackbar>
    </>
  );
}
